import React from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Contact } from '@/types/account'
import { contactsApiService, ContactCreationRequest } from '@/services/contactsApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Loader2, Plus, Trash2, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

const PHONE_TYPES = ['mobile', 'home', 'work'] as const
type PhoneType = typeof PHONE_TYPES[number]

const TYPE_LABELS: Record<PhoneType, string> = {
  mobile: 'Mobile',
  home: 'Home',
  work: 'Work',
}

interface ContactFormProps {
  /** Contact to edit, or undefined for creating new contact */
  contact?: Contact
  /** Pre-fill the first phone number field (used when creating from call history) */
  initialPhoneNumber?: string
  /** Callback after successful save */
  onSuccess?: () => void
  /** Callback for errors */
  onError?: (error: string) => void
}

/**
 * ContactForm component for creating or updating a contact
 */
const ContactForm: React.FC<ContactFormProps> = ({ contact, initialPhoneNumber, onSuccess, onError }) => {
  const { t } = useTranslation('app')
  const [isLoading, setIsLoading] = React.useState(false)

  const form = useForm<ContactCreationRequest>({
    defaultValues: {
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
      phoneNumbers: contact?.phoneNumbers?.length
        ? contact.phoneNumbers
        : [{ number: initialPhoneNumber ?? '', type: 'mobile' }],
    }
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'phoneNumbers',
  })

  const onSubmit = async (data: ContactCreationRequest) => {
    try {
      setIsLoading(true)

      if (contact) {
        await contactsApiService.updateContact(contact.id, data)
      } else {
        await contactsApiService.createContact(data)
      }

      onSuccess?.()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : (contact ? t('contacts.updateError') : t('contacts.createError'))
      toast.error(errorMsg)
      onError?.(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('contacts.firstName')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('contacts.firstNamePlaceholder')}
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('contacts.lastName')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('contacts.lastNamePlaceholder')}
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Phone numbers list */}
        <div className="space-y-2">
          <FormLabel>{t('contacts.phoneNumber')}</FormLabel>
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              {/* Type selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-24 shrink-0 justify-between px-2"
                    disabled={isLoading}
                  >
                    <span className="capitalize text-xs">
                      {TYPE_LABELS[form.watch(`phoneNumbers.${index}.type`) as PhoneType] ?? 'Mobile'}
                    </span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {PHONE_TYPES.map((type) => (
                    <DropdownMenuItem
                      key={type}
                      onClick={() => form.setValue(`phoneNumbers.${index}.type`, type)}
                      className={cn(
                        form.watch(`phoneNumbers.${index}.type`) === type && 'font-medium'
                      )}
                    >
                      {TYPE_LABELS[type]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Number input */}
              <FormField
                control={form.control}
                name={`phoneNumbers.${index}.number`}
                rules={{ required: t('contacts.phoneNumberRequired', 'Phone number is required') }}
                render={({ field: numField }) => (
                  <FormItem className="flex-1 space-y-0">
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder={t('contacts.phonePlaceholder')}
                        {...numField}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Remove button — only show when more than one entry */}
              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(index)}
                  disabled={isLoading}
                  className="shrink-0 text-destructive hover:text-destructive"
                  title={t('contacts.removePhoneNumber', 'Remove number')}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}

          {/* Add number button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ number: '', type: 'mobile' })}
            disabled={isLoading}
            className="w-full mt-1"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('contacts.addPhoneNumber', 'Add number')}
          </Button>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            type="submit"
            className="flex-1"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {contact ? t('contacts.update') : t('contacts.create')}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default ContactForm
